/* Atlas theme — storefront behaviour. No external dependencies. */
(() => {
  'use strict';

  const theme = window.theme || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------
     Utilities
  ------------------------------------------------------------------ */
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe, summary';

  const trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  const toast = (message) => {
    const el = qs('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => (el.hidden = true), 3200);
  };

  /* ------------------------------------------------------------------
     Overlay + drawer manager (menu, cart, filters, search)
  ------------------------------------------------------------------ */
  let overlay;
  const getOverlay = () => {
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'overlay';
      overlay.hidden = true;
      overlay.addEventListener('click', () => Drawer.closeAll());
      document.body.appendChild(overlay);
    }
    return overlay;
  };

  class Drawer extends HTMLElement {
    static openInstance = null;

    static closeAll() {
      if (Drawer.openInstance) Drawer.openInstance.close();
    }

    connectedCallback() {
      this.panel = this.firstElementChild;
      this.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
      qsa('[data-drawer-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
    }

    open(opener) {
      if (Drawer.openInstance && Drawer.openInstance !== this) Drawer.openInstance.close(false);
      this.opener = opener || document.activeElement;
      const ov = getOverlay();
      ov.hidden = false;
      requestAnimationFrame(() => ov.classList.add('is-visible'));
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      document.body.dataset.openDrawer = this.id;
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      this.releaseTrap = trapFocus(this.panel);
      const focusTarget = qs('[data-autofocus]', this.panel) || qs(FOCUSABLE, this.panel);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 60);
      Drawer.openInstance = this;
    }

    close(restoreFocus = true) {
      if (!this.panel.classList.contains('is-open')) return;
      this.panel.classList.remove('is-open');
      if (!this.panel.hasAttribute('data-keep-aria')) this.panel.setAttribute('aria-hidden', 'true');
      const ov = getOverlay();
      ov.classList.remove('is-visible');
      setTimeout(() => {
        if (!Drawer.openInstance) ov.hidden = true;
      }, 300);
      document.body.classList.remove('is-locked');
      delete document.body.dataset.openDrawer;
      if (this.releaseTrap) this.releaseTrap();
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      Drawer.openInstance = null;
    }
  }
  customElements.define('drawer-component', Drawer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && Drawer.openInstance) Drawer.openInstance.close();
  });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer) return;
    // Cart icon falls back to the cart page when JS drawer is disabled.
    e.preventDefault();
    drawer.open(opener);
  });

  /* ------------------------------------------------------------------
     Header: scroll state, desktop mega menus
  ------------------------------------------------------------------ */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.header-wrapper') || this;
      const onScroll = () => this.wrapper.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      this.menuButtons = qsa('[data-mega-toggle]', this);
      this.menuButtons.forEach((btn) => {
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        btn.addEventListener('click', () => {
          // A mouse click straight after hover-open should keep the menu open, not toggle it shut.
          const justOpenedByHover = btn._hoverOpenedAt && Date.now() - btn._hoverOpenedAt < 600;
          this.toggle(btn, panel, justOpenedByHover ? true : undefined);
        });
        if (window.matchMedia('(hover: hover)').matches) {
          const item = btn.closest('.nav__item');
          let leaveTimer;
          item.addEventListener('mouseenter', () => {
            clearTimeout(leaveTimer);
            if (btn.getAttribute('aria-expanded') !== 'true') btn._hoverOpenedAt = Date.now();
            this.toggle(btn, panel, true);
          });
          item.addEventListener('mouseleave', () => {
            leaveTimer = setTimeout(() => this.toggle(btn, panel, false), 120);
          });
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const open = this.menuButtons.find((b) => b.getAttribute('aria-expanded') === 'true');
          if (open) {
            this.closeAll();
            open.focus();
          }
        }
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closeAll();
      });
      this.addEventListener('focusout', (e) => {
        if (e.relatedTarget && !this.contains(e.relatedTarget)) this.closeAll();
      });
    }

    toggle(btn, panel, force) {
      const open = typeof force === 'boolean' ? force : btn.getAttribute('aria-expanded') !== 'true';
      if (open) this.closeAll(btn);
      btn.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
    }

    closeAll(except) {
      this.menuButtons.forEach((b) => {
        if (b === except) return;
        b.setAttribute('aria-expanded', 'false');
        const p = document.getElementById(b.getAttribute('aria-controls'));
        if (p) p.hidden = true;
      });
    }
  }
  customElements.define('site-header', SiteHeader);

  /* Generic disclosure (mobile sub-menus, localization popovers) */
  class DisclosureToggle extends HTMLElement {
    connectedCallback() {
      this.button = qs(':scope > button', this);
      this.panel = document.getElementById(this.button.getAttribute('aria-controls'));
      this.button.addEventListener('click', () => this.set(this.button.getAttribute('aria-expanded') !== 'true'));
      if (this.hasAttribute('data-dismissable')) {
        document.addEventListener('click', (e) => !this.contains(e.target) && this.set(false));
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.button.getAttribute('aria-expanded') === 'true') {
            this.set(false);
            this.button.focus();
          }
        });
        this.addEventListener('focusout', (e) => e.relatedTarget && !this.contains(e.relatedTarget) && this.set(false));
      }
    }
    set(open) {
      this.button.setAttribute('aria-expanded', String(open));
      this.panel.hidden = !open;
    }
  }
  customElements.define('disclosure-toggle', DisclosureToggle);

  /* Localization: option buttons submit the Shopify localization form */
  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      qsa('[data-value]', this).forEach((btn) =>
        btn.addEventListener('click', () => {
          const input = qs(`input[name="${btn.dataset.input}"]`, this.form);
          input.value = btn.dataset.value;
          this.form.submit();
        })
      );
    }
  }
  customElements.define('localization-form', LocalizationForm);

  /* Announcement bar (manual controls, no autoplay) */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('.announcement__slide', this);
      this.index = 0;
      qsa('[data-step]', this).forEach((b) => b.addEventListener('click', () => this.go(this.index + Number(b.dataset.step))));
    }
    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => s.setAttribute('aria-hidden', String(n !== this.index)));
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ------------------------------------------------------------------
     Search modal + predictive search
  ------------------------------------------------------------------ */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.panel = this.firstElementChild;
      this.input = qs('input[type="search"]', this);
      qsa('[data-search-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-search-open]');
        if (opener) {
          e.preventDefault();
          this.open(opener);
        }
      });
    }
    open(opener) {
      this.opener = opener;
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('search-open');
      const ov = getOverlay();
      ov.hidden = false;
      requestAnimationFrame(() => ov.classList.add('is-visible'));
      this._overlayClose = () => this.close();
      ov.addEventListener('click', this._overlayClose, { once: true });
      this.releaseTrap = trapFocus(this.panel);
      setTimeout(() => this.input.focus(), 50);
    }
    close() {
      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('search-open');
      const ov = getOverlay();
      ov.classList.remove('is-visible');
      setTimeout(() => (ov.hidden = true), 300);
      if (this.releaseTrap) this.releaseTrap();
      if (this.opener) this.opener.focus();
    }
  }
  customElements.define('search-modal', SearchModal);

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      this.defaultContent = qs('[data-predictive-default]', this);
      this.abort = null;
      this.input.addEventListener('input', debounce(() => this.search(), 250));
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    }

    async search() {
      const term = this.input.value.trim();
      if (!term) {
        this.results.hidden = true;
        this.results.innerHTML = '';
        if (this.defaultContent) this.defaultContent.hidden = false;
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last'
      });
      try {
        const res = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const content = qs('#shopify-section-predictive-search', parseHTML(html));
        this.results.innerHTML = content ? content.innerHTML : '';
        this.results.hidden = false;
        if (this.defaultContent) this.defaultContent.hidden = true;
        this.input.setAttribute('aria-expanded', 'true');
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.results.hidden = true;
        }
      }
    }

    onKeydown(e) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key) || this.results.hidden) return;
      const options = qsa('[role="option"]', this.results);
      if (!options.length) return;
      const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (e.key === 'Enter') {
        if (current > -1) {
          e.preventDefault();
          options[current].click();
        }
        return;
      }
      e.preventDefault();
      let next = e.key === 'ArrowDown' ? current + 1 : current - 1;
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;
      options.forEach((o) => o.setAttribute('aria-selected', 'false'));
      options[next].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------
     Cart (drawer + page) using the Ajax Cart API and Section Rendering API
  ------------------------------------------------------------------ */
  const Cart = {
    sectionsToRender() {
      const ids = [];
      if (qs('#CartDrawer')) ids.push('cart-drawer');
      const main = qs('[data-main-cart]');
      if (main) ids.push(main.dataset.sectionId);
      return ids;
    },

    async request(url, body) {
      const sections = this.sectionsToRender();
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        const err = new Error(data.description || data.message || theme.strings.cartError);
        err.data = data;
        throw err;
      }
      return data;
    },

    // Describe the focused cart control so it can be re-focused after a re-render.
    focusKey() {
      const el = document.activeElement;
      const item = el && el.closest('[data-cart-item]');
      if (!item) return null;
      const items = qsa('[data-cart-item]', item.parentElement);
      return { scope: el.closest('#CartDrawer') ? '#CartDrawer' : '[data-main-cart]', index: items.indexOf(item), name: el.getAttribute('name') || (el.matches('[data-line-quantity]') ? 'qty' : 'remove') };
    },

    restoreFocus(key) {
      if (!key) return;
      const scope = qs(key.scope);
      if (!scope) return;
      const items = qsa('[data-cart-item]', scope);
      const item = items[Math.min(key.index, items.length - 1)];
      let target = null;
      if (item) {
        if (key.name === 'qty') target = qs('[data-line-quantity]', item);
        else if (key.name === 'remove') target = qs('[data-line-remove]', item);
        else target = qs(`button[name="${key.name}"]`, item);
      }
      target = target || qs('[data-drawer-close]', scope) || qs('h1', scope);
      if (target) target.focus({ preventScroll: true });
    },

    render(sections) {
      if (!sections) return;
      const focus = this.focusKey();
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-drawer') {
          const fresh = qs('[data-cart-drawer-inner]', doc);
          const current = qs('[data-cart-drawer-inner]');
          if (fresh && current) current.replaceWith(fresh);
        } else {
          const fresh = qs('[data-main-cart]', doc);
          const current = qs('[data-main-cart]');
          if (fresh && current) current.replaceWith(fresh);
        }
      });
      this.restoreFocus(focus);
    },

    updateCount(count) {
      qsa('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      qsa('[data-cart-count-label]').forEach((el) => {
        el.textContent = el.dataset[count === 1 ? 'one' : 'other'].replace('[count]', count);
      });
    },

    async refreshCount() {
      try {
        const res = await fetch(`${theme.routes.cart}.js`);
        const cart = await res.json();
        this.updateCount(cart.item_count);
        return cart;
      } catch (e) {
        return null;
      }
    },

    async change(line, quantity, itemEl) {
      if (itemEl) itemEl.classList.add('is-loading');
      try {
        const data = await this.request(`${theme.routes.cartChange}.js`, { line, quantity });
        this.render(data.sections);
        this.updateCount(data.item_count);
        // Shopify silently caps quantity at available stock — tell the shopper.
        const item = data.items[line - 1];
        if (quantity > 0 && item && item.quantity < quantity) {
          toast(theme.strings.quantityError.replace('[quantity]', item.quantity));
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (err) {
        if (itemEl) {
          itemEl.classList.remove('is-loading');
          const errEl = qs('[data-line-error]', itemEl);
          if (errEl) {
            errEl.textContent = err.message;
            errEl.hidden = false;
          }
        } else {
          toast(err.message);
        }
      }
    }
  };
  window.AtlasCart = Cart;

  /* Quantity stepper */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      qsa('button', this).forEach((b) =>
        b.addEventListener('click', (e) => {
          e.preventDefault();
          const step = Number(this.input.step) || 1;
          const min = Number(this.input.min) || 0;
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const value = Number(this.input.value) + (b.name === 'plus' ? step : -step);
          this.input.value = Math.min(Math.max(value, min), max);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* Line items in drawer + cart page (delegated, survives re-render) */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-quantity]');
    if (!input) return;
    const itemEl = input.closest('[data-cart-item]');
    debouncedChange(Number(input.dataset.line), Number(input.value), itemEl);
  });
  const debouncedChange = debounce((line, qty, el) => Cart.change(line, qty, el), 350);

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-line-remove]');
    if (!remove) return;
    e.preventDefault();
    Cart.change(Number(remove.dataset.line), 0, remove.closest('[data-cart-item]'));
  });

  /* Cart note (page + drawer) */
  document.addEventListener(
    'input',
    debounce((e) => {
      const note = e.target.closest('[data-cart-note]');
      if (!note) return;
      fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.value })
      });
    }, 500)
  );

  class CartDrawer extends Drawer {
    connectedCallback() {
      super.connectedCallback();
      document.addEventListener('cart:open', () => this.open(qs('[data-drawer-open="CartDrawer"]')));
    }
    open(opener) {
      super.open(opener);
      loadCartRecommendations(this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* Recommendations shown inside the cart drawer, based on the first line item */
  const loadCartRecommendations = async (root) => {
    const container = qs('[data-cart-recs]', root);
    if (!container || container.dataset.loadedFor === container.dataset.productId) return;
    const productId = container.dataset.productId;
    if (!productId) return;
    try {
      const url = `${theme.routes.productRecommendations}?product_id=${productId}&limit=6&section_id=cart-recommendations&intent=complementary`;
      let html = await (await fetch(url)).text();
      let fresh = qs('[data-cart-recs-list]', parseHTML(html));
      if (!fresh || !fresh.children.length) {
        html = await (await fetch(url.replace('intent=complementary', 'intent=related'))).text();
        fresh = qs('[data-cart-recs-list]', parseHTML(html));
      }
      if (!fresh) return;
      const inCart = (container.dataset.inCart || '').split(',');
      qsa('[data-product-id]', fresh).forEach((el) => inCart.includes(el.dataset.productId) && el.remove());
      qsa('[data-product-id]', fresh).slice(3).forEach((el) => el.remove());
      if (!fresh.children.length) return;
      qs('[data-cart-recs-target]', container).replaceChildren(...fresh.children);
      container.hidden = false;
      container.dataset.loadedFor = productId;
    } catch (e) {
      /* recommendations are optional */
    }
  };
  document.addEventListener('cart:updated', () => {
    const drawer = qs('#CartDrawer');
    if (drawer && Drawer.openInstance === drawer) loadCartRecommendations(drawer);
  });

  /* ------------------------------------------------------------------
     Product form (PDP, quick add, cart recommendations)
  ------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      this.error = qs('[data-form-error]', this);
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      if (theme.cartType !== 'drawer' && !this.hasAttribute('data-ajax-always')) return; // native POST to /cart/add
      e.preventDefault();
      const submitter = e.submitter || qs('[type="submit"]', this.form);
      const buttons = [qs('[type="submit"]', this.form), submitter, ...qsa(`[form="${this.form.id}"]`)].filter(Boolean);
      if (buttons.some((b) => b.getAttribute('aria-disabled') === 'true' || b.disabled)) return;
      buttons.forEach((b) => {
        b.classList.add('is-loading');
        b.setAttribute('aria-busy', 'true');
      });
      if (this.error) this.error.hidden = true;

      const formData = new FormData(this.form);
      const sections = Cart.sectionsToRender();
      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const res = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
        Cart.render(data.sections);
        const cart = await Cart.refreshCount();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        if (qs('#CartDrawer') && theme.cartType === 'drawer') {
          document.dispatchEvent(new CustomEvent('cart:open'));
        } else {
          toast(theme.strings.added);
        }
      } catch (err) {
        if (this.error) {
          this.error.textContent = err.message;
          this.error.hidden = false;
        } else {
          toast(err.message);
        }
      } finally {
        buttons.forEach((b) => {
          b.classList.remove('is-loading');
          b.removeAttribute('aria-busy');
        });
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------
     Variant picker: exact Shopify variant matching, availability marks,
     server-rendered price/stock via Section Rendering API
  ------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.url;
      this.data = JSON.parse(qs('script[type="application/json"]', this).textContent);
      this.section = this.closest('[data-product-section]') || document;
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"], select')) this.onChange();
      });
      this.markAvailability();
    }

    selectedOptions() {
      return qsa('[data-option-index]', this).map((group) => {
        const checked = qs('input:checked', group);
        if (checked) return checked.value;
        const select = qs('select', group);
        return select ? select.value : null;
      });
    }

    findVariant(options) {
      return this.data.variants.find((v) => v.options.every((val, i) => val === options[i])) || null;
    }

    markAvailability() {
      const selected = this.selectedOptions();
      qsa('[data-option-index]', this).forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        qsa('input[type="radio"]', group).forEach((input) => {
          // Cascading rule: a value is purchasable if some in-stock variant shares it
          // and matches every option selected *before* this one.
          const matchesEarlier = (v) => v.options[index] === input.value && v.options.slice(0, index).every((val, j) => val === selected[j]);
          const exists = this.data.variants.some(matchesEarlier);
          const available = this.data.variants.some((v) => v.available && matchesEarlier(v));
          input.classList.toggle('is-unavailable', !available);
          const label = input.nextElementSibling;
          if (label && label.dataset.baseLabel) {
            label.setAttribute('aria-label', !exists || !available ? `${label.dataset.baseLabel} – ${theme.strings.soldOut}` : label.dataset.baseLabel);
          }
        });
        const current = qs('input:checked', group);
        const display = qs('[data-selected-value]', group);
        if (current && display) display.textContent = current.value;
      });
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.findVariant(options);
      this.markAvailability();
      const idInputs = qsa('input[name="id"]', this.section);
      const buttons = qsa('[data-add-button]', this.section);

      if (!variant) {
        buttons.forEach((b) => this.setButton(b, false, theme.strings.unavailable));
        qsa('[data-variant-status]', this.section).forEach((el) => (el.textContent = theme.strings.unavailable));
        return;
      }

      idInputs.forEach((i) => {
        i.value = variant.id;
        i.dispatchEvent(new Event('change', { bubbles: true }));
      });
      buttons.forEach((b) => this.setButton(b, variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut));
      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (variant.featured_media_id) {
        document.dispatchEvent(new CustomEvent('variant:media', { detail: { mediaId: variant.featured_media_id, sectionId: this.sectionId } }));
      }
      this.renderSection(variant.id);
    }

    setButton(button, enabled, text) {
      const label = qs('[data-add-label]', button) || button;
      label.textContent = text;
      button.disabled = !enabled;
      button.setAttribute('aria-disabled', String(!enabled));
    }

    async renderSection(variantId) {
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      try {
        const res = await fetch(`${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`, { signal: this.abort.signal });
        const doc = parseHTML(await res.text());
        qsa('[data-dynamic]', this.section).forEach((el) => {
          const fresh = qs(`[data-dynamic="${el.dataset.dynamic}"]`, doc);
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
      } catch (e) {
        /* price stays as previously rendered; button state already updated */
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------
     Product gallery: swipe, thumbnails, deferred video, zoom
  ------------------------------------------------------------------ */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = qs('[data-gallery-viewport]', this);
      this.slides = qsa('[data-media-id]', this.viewport);
      this.thumbs = qsa('[data-thumb]', this);
      this.dots = qsa('[data-dot]', this);
      this.counter = qs('[data-counter]', this);
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goTo(i)));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.goTo(i)));
      qsa('[data-gallery-step]', this).forEach((b) => b.addEventListener('click', () => this.goTo(this.index + Number(b.dataset.galleryStep))));
      this.index = 0;

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setActive(this.slides.indexOf(entry.target));
          });
        },
        { root: this.viewport, threshold: [0.6] }
      );
      this.slides.forEach((s) => io.observe(s));

      document.addEventListener('variant:media', (e) => {
        if (e.detail.sectionId !== this.dataset.sectionId) return;
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(e.detail.mediaId));
        if (i > -1) this.goTo(i);
      });

      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.goTo(this.index + 1);
        if (e.key === 'ArrowLeft') this.goTo(this.index - 1);
      });

      this.dialog = qs('[data-zoom-dialog]', this);
      qsa('[data-zoom-open]', this).forEach((btn) => btn.addEventListener('click', () => this.openZoom(btn.dataset.zoomOpen)));
      if (this.dialog) {
        qs('[data-zoom-close]', this.dialog).addEventListener('click', () => this.dialog.close());
        qsa('img', this.dialog).forEach((img) =>
          img.addEventListener('click', (e) => {
            const r = img.getBoundingClientRect();
            img.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
            img.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
            img.classList.toggle('is-zoomed');
          })
        );
      }
    }

    goTo(i) {
      const n = (i + this.slides.length) % this.slides.length;
      const slide = this.slides[n];
      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      this.setActive(n);
    }

    setActive(i) {
      if (i < 0 || i === this.index) return;
      this.slides[this.index] && this.pauseMedia(this.slides[this.index]);
      this.index = i;
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
      this.dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
    }

    pauseMedia(slide) {
      qsa('video', slide).forEach((v) => v.pause());
      qsa('iframe', slide).forEach((f) => {
        // Reset external players so they stop playing when swiped away.
        if (f.src) f.src = f.src; // eslint-disable-line no-self-assign
      });
    }

    openZoom(mediaId) {
      if (!this.dialog) return;
      this.dialog.showModal();
      const target = qs(`[data-zoom-media="${mediaId}"]`, this.dialog);
      if (target) target.scrollIntoView({ block: 'start' });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* Click-to-load video (Shopify-hosted, YouTube, Vimeo) to keep pages light */
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      const btn = qs('[data-deferred-play]', this);
      if (btn) btn.addEventListener('click', () => this.load());
    }
    load() {
      if (this.loaded) return;
      const tpl = qs('template', this);
      if (!tpl) return;
      const content = tpl.content.cloneNode(true);
      const target = qs('[data-deferred-target]', this) || this;
      target.innerHTML = '';
      target.appendChild(content);
      this.loaded = true;
      const video = qs('video', target);
      if (video) video.play().catch(() => {});
      const frame = qs('iframe', target);
      if (frame) frame.focus();
    }
  }
  customElements.define('deferred-media', DeferredMedia);

  /* Sticky add-to-cart bar (mobile/tablet) */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.getElementById(this.dataset.anchor);
      if (!anchor) return;
      this.bar = this.firstElementChild;
      // Visible whenever the main buy buttons have scrolled above the viewport.
      new IntersectionObserver(([e]) => {
        const visible = !e.isIntersecting && e.boundingClientRect.top < 0;
        this.bar.classList.toggle('is-visible', visible);
        this.bar.setAttribute('aria-hidden', String(!visible));
        document.body.classList.toggle('has-sticky-atc', visible);
        qsa('button, a', this.bar).forEach((el) => (el.tabIndex = visible ? 0 : -1));
      }).observe(anchor);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* Modal opener for size / colour guides */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.modalOpen);
      if (dialog && dialog.showModal) {
        e.preventDefault();
        dialog.showModal();
      }
    }
    const closer = e.target.closest('[data-modal-close]');
    if (closer) closer.closest('dialog').close();
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('modal')) e.target.close();
  });

  /* ------------------------------------------------------------------
     Product recommendations (related / complementary)
  ------------------------------------------------------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(this);
    }
    async load() {
      try {
        const res = await fetch(this.dataset.url);
        const doc = parseHTML(await res.text());
        const fresh = qs('product-recommendations', doc);
        if (fresh && qs('[data-rec-item]', fresh)) {
          this.innerHTML = fresh.innerHTML;
          this.hidden = false;
          initReveal(this);
        } else if (this.useFallback()) {
          return;
        } else {
          this.closest('.shopify-section')?.classList.add('is-empty');
          this.remove();
        }
      } catch (e) {
        if (!this.useFallback()) this.remove();
      }
    }
    useFallback() {
      const tpl = qs('template[data-rec-fallback]', this);
      if (!tpl || !tpl.content.querySelector('[data-rec-item]')) return false;
      this.innerHTML = tpl.innerHTML;
      this.hidden = false;
      initReveal(this);
      return true;
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------
     Collection & search filters (Shopify Search & Discovery facets)
  ------------------------------------------------------------------ */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', (e) => {
        if (e.target.closest('form[data-facet-form]')) {
          e.target.type === 'number' ? this.debouncedSubmit() : this.submit();
        }
      });
      this.addEventListener('submit', (e) => {
        if (e.target.matches('form[data-facet-form]')) {
          e.preventDefault();
          this.submit();
        }
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (!link) return;
        e.preventDefault();
        this.render(new URL(link.href, window.location.origin).searchParams.toString());
      });
      this.debouncedSubmit = debounce(() => this.submit(), 600);
      window.addEventListener('popstate', () => this.render(window.location.search.slice(1), false));
    }

    paramsFromForms() {
      const params = new URLSearchParams();
      qsa('form[data-facet-form]', this).forEach((form) => {
        for (const [key, value] of new FormData(form)) {
          if (value === '' && key.startsWith('filter.v.price')) continue;
          if (!params.getAll(key).includes(value)) params.append(key, value);
        }
      });
      return params.toString();
    }

    submit() {
      this.render(this.paramsFromForms());
    }

    async render(search, push = true) {
      const activeId = document.activeElement && document.activeElement.id;
      const grid = qs('[data-results]', this);
      grid && grid.classList.add('is-loading');
      try {
        const params = new URLSearchParams(search);
        params.set('section_id', this.sectionId);
        const res = await fetch(`${window.location.pathname}?${params}`);
        const doc = parseHTML(await res.text());
        ['[data-results]', '[data-facets]', '[data-active-filters]', '[data-results-count]', '[data-sort]'].forEach((sel) => {
          const fresh = qs(sel, doc);
          qsa(sel, this).forEach((el) => {
            if (fresh) el.innerHTML = fresh.innerHTML;
          });
        });
        // Keep keyboard focus on the control the shopper just used.
        if (activeId) {
          const el = document.getElementById(activeId);
          if (el) el.focus({ preventScroll: true });
        }
        if (push) window.history.pushState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
        initReveal(this);
      } catch (e) {
        toast(theme.strings.cartError);
      } finally {
        const g = qs('[data-results]', this);
        g && g.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ------------------------------------------------------------------
     Before / after comparison
  ------------------------------------------------------------------ */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      const range = qs('input[type="range"]', this);
      const frame = qs('.ba__frame', this);
      if (!range || !frame) return;
      const set = () => frame.style.setProperty('--pos', `${range.value}%`);
      range.addEventListener('input', set);
      set();
    }
  }
  customElements.define('before-after', BeforeAfter);


  /* ------------------------------------------------------------------
     Hair finder quiz: concern → colour → length → matching real products
  ------------------------------------------------------------------ */
  const COLOUR_RULES = {
    black: /black|jet|ebony|\b1b\b/i,
    darkbrown: /dark brown|black brown|black\/ ?brown|coffee|chocolate|espresso/i,
    brown: /brown|chestnut|mocha|coffee|bronze|caramel brown/i,
    light: /highlight|balayage|ombr|honey|blonde|caramel|champagne|light brown/i,
    grey: /grey|gray|salt|pepper|silver|white/i
  };
  const COLOUR_OPTION = /colou?r|shade/i;
  const LENGTH_OPTION = /length|size/i;
  const inches = (value) => {
    const nums = (String(value).match(/\d+(\.\d+)?/g) || []).map(Number).filter((n) => n >= 6 && n <= 36);
    return nums.length ? Math.max(...nums) : null;
  };
  const lengthMatches = (value, want) => {
    const n = inches(value);
    if (n == null) return false;
    if (want === 'short') return n <= 12;
    if (want === 'medium') return n > 12 && n <= 16;
    if (want === 'long') return n > 16;
    return true;
  };

  class HairFinder extends HTMLElement {
    connectedCallback() {
      // Translations arrive HTML-escaped (e.g. &#39;); decode them once for use as plain text.
      const decode = (v) => { const t = document.createElement('textarea'); t.innerHTML = v; return t.value; };
      const deep = (o) => (typeof o === 'string' ? decode(o) : Object.fromEntries(Object.entries(o).map(([k, v]) => [k, deep(v)])));
      this.strings = deep(JSON.parse(qs('[data-finder-strings]', this).textContent));
      this.steps = qsa('[data-step]', this);
      this.answers = {};
      this.cache = {};
      this.back = qs('[data-back]', this);
      this.restart = qs('[data-restart]', this);
      this.progress = qs('[data-progress]', this);
      this.addEventListener('change', (e) => {
        const input = e.target;
        if (input.type !== 'radio') return;
        const step = Number(input.closest('[data-step]').dataset.step);
        this.answers[step] = input;
        setTimeout(() => this.show(step + 1), 180); // brief pause so the choice visibly registers
      });
      this.back.addEventListener('click', () => this.show(Math.max(1, this.current - 1)));
      this.restart.addEventListener('click', () => {
        this.answers = {};
        qsa('input[type="radio"]', this).forEach((i) => (i.checked = false));
        this.show(1);
      });
      this.show(1, false);
    }

    show(n, focus = true) {
      this.current = n;
      this.steps.forEach((s) => (s.hidden = Number(s.dataset.step) !== n));
      this.back.hidden = n === 1;
      this.restart.hidden = n !== 4;
      this.progress.style.width = `${Math.min(n - 1, 3) / 3 * 100}%`;
      if (n === 4) this.results();
      if (focus) {
        const heading = qs(`[data-step="${n}"] .finder__q`, this);
        if (heading) heading.focus({ preventScroll: true });
        const top = this.getBoundingClientRect().top;
        if (top < 0 || top > window.innerHeight * 0.6) this.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }
    }

    async load(handle) {
      if (this.cache[handle]) return this.cache[handle];
      const res = await fetch(`${theme.routes.root.replace(/\/$/, '')}/collections/${handle}?section_id=hair-finder-data`);
      if (!res.ok) throw new Error(res.status);
      const data = JSON.parse(qs('[data-finder-products]', parseHTML(await res.text())).textContent);
      this.cache[handle] = data;
      return data;
    }

    score(product, colour, length) {
      const ci = product.options.findIndex((o) => COLOUR_OPTION.test(o));
      const li = product.options.findIndex((o) => LENGTH_OPTION.test(o));
      let best = null;
      for (const v of product.variants) {
        if (!v.a) continue;
        let s = 1;
        if (colour !== 'any' && ci > -1 && COLOUR_RULES[colour].test(v.o[ci])) s += 2;
        if (length !== 'any' && li > -1 && lengthMatches(v.o[li], length)) s += 1;
        if (!best || s > best.s) best = { s, id: v.id };
      }
      return best;
    }

    async results() {
      const list = qs('[data-results]', this);
      const loading = qs('[data-loading]', this);
      const note = qs('[data-note]', this);
      const concern = this.answers[1];
      const colour = this.answers[2] ? this.answers[2].value : 'any';
      const length = this.answers[3] ? this.answers[3].value : 'any';
      qs('[data-summary]', this).textContent = this.strings.summary
        .replace('[concern]', concern.dataset.label)
        .replace('[colour]', this.strings.colours[colour])
        .replace('[length]', this.strings.lengths[length]);
      qs('[data-view-collection]', this).href = `/collections/${concern.value}`;
      list.innerHTML = '';
      note.hidden = true;
      loading.hidden = false;
      try {
        const products = await this.load(concern.value);
        const scored = products
          .map((p, i) => ({ p, i, m: this.score(p, colour, length) }))
          .filter((x) => x.m)
          .sort((a, b) => b.m.s - a.m.s || a.i - b.i);
        const top = scored.slice(0, 6);
        const wanted = 1 + (colour !== 'any' ? 2 : 0) + (length !== 'any' ? 1 : 0);
        if (!top.length) {
          note.textContent = this.strings.none;
          note.hidden = false;
        } else if (top[0].m.s < wanted) {
          note.textContent = this.strings.noExact;
          note.hidden = false;
        }
        list.innerHTML = top.map(({ p, m }, i) => `
          <li class="finder__item">
            <a class="finder__product" href="${p.url}?variant=${m.id}">
              <span class="finder__img media">${p.image ? `<img src="${p.image}" alt="" loading="lazy" width="600" height="600">` : ''}
                ${i === 0 && m.s === wanted && wanted > 1 ? `<span class="badge badge--sticker finder__best">${this.strings.bestMatch}</span>` : ''}
              </span>
              <span class="finder__ptitle">${p.title.replace(/</g, '&lt;')}</span>
              <span class="price${p.onSale ? ' price--on-sale' : ''}"><span class="price__current">${p.varies ? this.strings.from.replace('[price]', p.price) : p.price}</span></span>
            </a>
          </li>`).join('');
      } catch (e) {
        note.textContent = this.strings.error;
        note.hidden = false;
      } finally {
        loading.hidden = true;
      }
    }
  }
  customElements.define('hair-finder', HairFinder);


  /* Floating "Find your match" launcher (not a pop-up: small, dismissible, never covers content) */
  class QuizLauncher extends HTMLElement {
    connectedCallback() {
      try { if (sessionStorage.getItem('atlas-quiz-dismissed')) return; } catch (e) { /* storage blocked */ }
      const quiz = document.getElementById('find-your-match');
      let quizVisible = false;
      const update = () => { this.hidden = quizVisible || window.scrollY < 400 || document.body.dataset.openDrawer; };
      window.addEventListener('scroll', update, { passive: true });
      if (quiz) new IntersectionObserver(([e]) => { quizVisible = e.isIntersecting; update(); }).observe(quiz);
      qs('.quiz-fab__close', this).addEventListener('click', () => {
        this.remove();
        try { sessionStorage.setItem('atlas-quiz-dismissed', '1'); } catch (e) { /* storage blocked */ }
      });
      update();
    }
  }
  customElements.define('quiz-launcher', QuizLauncher);

  /* ------------------------------------------------------------------
     First-visit offer pop-up (timer counts from the start of the visit, plus desktop exit intent)
  ------------------------------------------------------------------ */
  const store = {
    get: (area, key) => { try { return window[area].getItem(key); } catch (e) { return null; } },
    set: (area, key, value) => { try { window[area].setItem(key, value); } catch (e) { /* storage blocked */ } }
  };

  class WelcomePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = qs('dialog', this);
      if (!this.dialog || !this.dialog.showModal) return;
      qsa('[data-welcome-close]', this).forEach((b) => b.addEventListener('click', () => this.dialog.close()));
      this.dialog.addEventListener('close', () => this.onClose());
      const form = qs('form', this);
      if (form) form.addEventListener('submit', () => store.set('sessionStorage', 'atlas-welcome-submitted', '1'));
      const copy = qs('[data-welcome-copy]', this);
      if (copy) copy.addEventListener('click', () => this.copyCode(copy));

      if (this.hasAttribute('data-design-mode')) {
        document.addEventListener('shopify:section:select', (e) => e.target.contains(this) && this.open());
        document.addEventListener('shopify:section:deselect', (e) => e.target.contains(this) && this.dialog.close());
        return;
      }

      // Back from the sign-up post (success or error): reopen so they see the code / the problem
      const submitted = store.get('sessionStorage', 'atlas-welcome-submitted');
      if (submitted && qs('[data-welcome-success]', this)) {
        store.set('localStorage', 'atlas-welcome-done', '1');
        store.set('sessionStorage', 'atlas-welcome-submitted', '');
        this.open();
        return;
      }
      if (submitted && qs('[data-welcome-error]', this)) {
        store.set('sessionStorage', 'atlas-welcome-submitted', '');
        this.open();
        return;
      }

      if (store.get('localStorage', 'atlas-welcome-done')) return;
      if (Number(store.get('localStorage', 'atlas-welcome-snooze') || 0) > Date.now()) return;
      if (/[?&]customer_posted=true/.test(location.search)) return;
      this.arm();
    }

    arm() {
      let start = Number(store.get('sessionStorage', 'atlas-visit-start'));
      if (!start) {
        start = Date.now();
        store.set('sessionStorage', 'atlas-visit-start', String(start));
      }
      const wait = Math.max(Number(this.dataset.delay || 15) * 1000 - (Date.now() - start), 1500);
      this.timer = setTimeout(() => this.tryOpen(), wait);

      if (this.dataset.exitIntent === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.onExit = (e) => { if (!e.relatedTarget && e.clientY <= 0) this.tryOpen(); };
        setTimeout(() => document.addEventListener('mouseout', this.onExit), 3000);
      }
    }

    // Never interrupt an open cart/menu/search drawer or another dialog: retry shortly
    tryOpen() {
      const busy = document.body.dataset.openDrawer || document.body.classList.contains('search-open') || qs('dialog[open]');
      if (busy) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.tryOpen(), 4000);
        return;
      }
      this.disarm();
      this.open();
    }

    disarm() {
      clearTimeout(this.timer);
      if (this.onExit) document.removeEventListener('mouseout', this.onExit);
    }

    open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      const field = qs('input[type="email"]', this.dialog);
      if (field && window.matchMedia('(pointer: fine)').matches) field.focus();
    }

    onClose() {
      this.disarm();
      if (this.hasAttribute('data-design-mode') || store.get('localStorage', 'atlas-welcome-done')) return;
      const days = Number(this.dataset.snoozeDays || 7);
      store.set('localStorage', 'atlas-welcome-snooze', String(Date.now() + days * 864e5));
    }

    copyCode(button) {
      const code = qs('[data-welcome-code]', this).textContent.trim();
      if (!navigator.clipboard) return;
      // The toast sits under the modal's top layer, so confirm on the button itself
      navigator.clipboard.writeText(code).then(() => {
        const label = button.textContent;
        button.textContent = button.dataset.copied;
        setTimeout(() => (button.textContent = label), 2000);
      }, () => {});
    }
  }
  customElements.define('welcome-popup', WelcomePopup);

  /* UGC wall: muted clips play only while on screen (and never with reduced motion) */
  class UgcWall extends HTMLElement {
    connectedCallback() {
      const videos = qsa('video', this);
      if (!videos.length || reducedMotion.matches || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) target.play().catch(() => {});
          else target.pause();
        }),
        { threshold: 0.5 }
      );
      videos.forEach((v) => this.observer.observe(v));
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }
  customElements.define('ugc-wall', UgcWall);

  /* ------------------------------------------------------------------
     Scroll reveal (respects reduced motion + theme setting)
  ------------------------------------------------------------------ */
  let revealObserver;
  const initReveal = (root = document) => {
    const items = qsa('.reveal:not(.is-revealed)', root);
    if (!items.length) return;
    if (reducedMotion.matches || document.body.classList.contains('no-animations') || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    }
    items.forEach((el) => revealObserver.observe(el));
  };

  document.addEventListener('DOMContentLoaded', () => initReveal());
  if (document.readyState !== 'loading') initReveal();
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  /* Theme editor: open the drawer/menu blocks when selected */
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector('#CartDrawer')) qs('#CartDrawer').open();
  });
})();
